import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  CubeIcon,
  CheckCircleIcon,
  CodeBracketIcon,
  TagIcon,
  CalendarIcon,
  FolderIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { BuiltImagesResponse, BuiltImage, Project } from '../types/api';
import Logger from '../utils/logger';

const BuiltImagesPage: React.FC = () => {
  const { t } = (useTranslation as any)('builds');
  const [imagesData, setImagesData] = useState<BuiltImagesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [limit] = useState(20);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);

  useEffect(() => {
    apiService.getProjects().then(setProjects).catch(Logger.error);
  }, []);

  useEffect(() => {
    setSelectedImageIds(new Set());
    loadBuiltImages();
  }, [currentPage, selectedProjectId]);

  const loadBuiltImages = async () => {
    try {
      setLoading(true);
      const data = await apiService.getBuiltImages({
        limit,
        offset: currentPage * limit,
        status_filter: 'success',
        successful_only: true,
        project_id: selectedProjectId || undefined
      });
      setImagesData(data);
    } catch (error: unknown) {
      Logger.error('Erreur chargement images:', error);
      toast.error(t('images.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
  };

  const getStatusText = () => {
    return t('images.success');
  };

  const getStatusColor = () => {
    return 'text-green-400 bg-green-500/12';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('images.copied'));
    } catch (error) {
      toast.error(t('images.copyError'));
    }
  };

  const sanitizeRepositoryUrl = (url: string): string => {
    // Remove credentials from URL (oauth2:token@host -> host)
    try {
      const urlObj = new URL(url);
      urlObj.username = '';
      urlObj.password = '';
      return urlObj.toString();
    } catch {
      return '#';
    }
  };

  const deleteImage = async (buildId: string) => {
    if (!window.confirm(t('images.deleteConfirm') as string)) {
      return;
    }
    try {
      await apiService.deleteBuiltImage(buildId);
      toast.success(t('images.deleteSuccess'));
      loadBuiltImages();
    } catch (error: unknown) {
      Logger.error('Erreur suppression image:', error);
      toast.error(t('images.deleteError'));
    }
  };

  const toggleImageSelection = (buildId: string) => {
    setSelectedImageIds((prev) => {
      const next = new Set(prev);
      if (next.has(buildId)) {
        next.delete(buildId);
      } else {
        next.add(buildId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!imagesData) return;
    if (selectedImageIds.size === imagesData.builds.length && imagesData.builds.length > 0) {
      setSelectedImageIds(new Set());
    } else {
      setSelectedImageIds(new Set(imagesData.builds.map((img) => img.build_id)));
    }
  };

  const deleteSelectedImages = async () => {
    if (selectedImageIds.size === 0) return;
    if (!window.confirm(t('images.deleteMultipleConfirm', { count: selectedImageIds.size }) as string)) return;

    setDeletingSelected(true);
    const results = await Promise.allSettled(
      Array.from(selectedImageIds).map((id) => apiService.deleteBuiltImage(id))
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      toast.error(t('images.deletionsError', { count: failed }));
    } else {
      toast.success(t('images.deletionsSuccess', { count: selectedImageIds.size }));
    }
    setSelectedImageIds(new Set());
    setDeletingSelected(false);
    loadBuiltImages();
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
        <p className="mt-4 text-text-tertiary">{t('images.loading')}</p>
      </div>
    );
  }

  return (
    <>
          {/* Header */}
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-text-primary sm:text-3xl sm:truncate">
                {t('images.title')}
              </h2>
              <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                <div className="mt-2 flex items-center text-sm text-text-tertiary">
                  <CubeIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-text-quaternary" />
                  {t('images.imageCount', { count: imagesData?.statistics.total_builds || 0 })}
                </div>
              </div>
            </div>
          </div>

          {/* Project Scope Selector */}
          {projects.length > 0 && (
            <div className="mt-6 flex items-center gap-3">
              <label className="text-sm font-medium text-text-secondary">
                {t('images.scope')}
              </label>
              <select
                value={selectedProjectId || ''}
                onChange={(e) => { setSelectedProjectId(e.target.value || null); setCurrentPage(0); }}
                className="block w-64 border border-border-1 rounded-md shadow-card py-2 px-3 bg-surface-3 text-text-primary text-sm focus:outline-none focus:ring-accent-500 focus:border-accent-500"
              >
                <option value="">{t('images.myImages')}</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {t('images.projectImages', { name: p.nom })}
                  </option>
                ))}
              </select>
              {selectedProjectId && (
                <span className="text-xs text-text-tertiary">
                  {t('images.showsAllMembers')}
                </span>
              )}
            </div>
          )}

          {/* Statistiques */}
          {imagesData && (
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2">
              <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircleIcon className="h-6 w-6 text-green-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-text-tertiary truncate">
                          {t('images.success')}
                        </dt>
                        <dd className="text-lg font-medium text-text-primary">
                          {imagesData.statistics.successful_builds}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-surface-2 overflow-hidden shadow-card rounded-card">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <TagIcon className="h-6 w-6 text-accent-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-text-tertiary truncate">
                          {t('images.successRate')}
                        </dt>
                        <dd className="text-lg font-medium text-text-primary">
                          {imagesData.statistics.success_rate.toFixed(1)}%
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Liste des images */}
          <div className="mt-8">
            {!imagesData || imagesData.builds.length === 0 ? (
              <div className="text-center py-12">
                <CubeIcon className="mx-auto h-12 w-12 text-text-quaternary" />
                <h3 className="mt-2 text-sm font-medium text-text-primary">{t('images.noImages')}</h3>
                <p className="mt-1 text-sm text-text-tertiary">
                  {t('images.noImagesFound')}
                </p>
              </div>
            ) : (
              <div className="bg-surface-2 shadow overflow-hidden sm:rounded-md">
                {/* Selection bar */}
                {selectedImageIds.size > 0 && (
                  <div className="flex items-center justify-between px-6 py-3 bg-blue-500/12 border-b border-border-1">
                    <span className="text-sm text-blue-400">
                      {t('images.selected', { count: selectedImageIds.size })}
                    </span>
                    <button
                      onClick={deleteSelectedImages}
                      disabled={deletingSelected}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-md"
                    >
                      {deletingSelected ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 mr-1.5 animate-spin" />
                          {t('images.deleting')}
                        </>
                      ) : (
                        <>
                          <TrashIcon className="w-4 h-4 mr-1.5" />
                          {t('images.deleteSelection')}
                        </>
                      )}
                    </button>
                  </div>
                )}
                {/* Select all header */}
                <div className="flex items-center px-4 py-2 border-b border-border-1 bg-surface-3">
                  <input
                    type="checkbox"
                    checked={imagesData.builds.length > 0 && selectedImageIds.size === imagesData.builds.length}
                    onChange={toggleSelectAll}
                    disabled={imagesData.builds.length === 0}
                    className="h-4 w-4 text-accent-600 border-border-1 rounded focus:ring-accent-500 mr-3"
                  />
                  <span className="text-xs text-text-tertiary">
                    {t('images.selectAll', { count: imagesData.builds.length })}
                  </span>
                </div>
                <ul className="divide-y divide-border-1">
                  {imagesData.builds.map((image: BuiltImage) => (
                    <li key={image.build_id} className={selectedImageIds.has(image.build_id) ? 'bg-accent-500/8' : ''}>
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 mr-3">
                              <input
                                type="checkbox"
                                checked={selectedImageIds.has(image.build_id)}
                                onChange={() => toggleImageSelection(image.build_id)}
                                className="h-4 w-4 text-accent-600 border-border-1 rounded focus:ring-accent-500"
                              />
                            </div>
                            <div className="flex-shrink-0 mr-4">
                              {getStatusIcon()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center">
                                <CubeIcon className="flex-shrink-0 h-5 w-5 text-text-quaternary mr-2" />
                                <p className="text-sm font-medium text-text-primary truncate">
                                  {image.application_name}
                                </p>
                                <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}>
                                  {getStatusText()}
                                </span>
                                {image.is_standalone && (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-purple-400 bg-purple-500/12">
                                    {t('images.standalone')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0 flex items-center space-x-2">
                            <button
                              onClick={() => copyToClipboard(image.image_url)}
                              className="p-2 text-text-quaternary hover:text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-500 rounded-md"
                              title={t('images.copyImageUrl') as string}
                            >
                              <DocumentDuplicateIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteImage(image.build_id)}
                              className="p-2 text-red-400 hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 rounded-md"
                              title={t('images.deleteImage') as string}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex sm:space-x-6">
                            <div className="flex items-center text-sm text-text-tertiary">
                              <TagIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-text-quaternary" />
                              <p>{image.image_tag}</p>
                            </div>
                            {image.project && (
                              <div className="flex items-center text-sm text-text-tertiary">
                                <FolderIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-text-quaternary" />
                                <Link 
                                  to={`/projects/${image.project.id}`}
                                  className="text-accent-600 hover:text-accent-500"
                                >
                                  {image.project.name}
                                </Link>
                              </div>
                            )}
                            <div className="flex items-center text-sm text-text-tertiary">
                              <CodeBracketIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-text-quaternary" />
                              <p>{image.repository.branch}</p>
                            </div>
                            <div className="flex items-center text-sm text-text-tertiary">
                              <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-text-quaternary" />
                              <p>{formatDate(image.build_info.created_at)}</p>
                            </div>
                          </div>
                        </div>

                        {/* Image URL */}
                        <div className="mt-3">
                          {image.image_url ? (
                            <div className="bg-surface-3 rounded-md p-3">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-mono text-text-secondary truncate flex-1 mr-2">
                                  {image.image_url}
                                </p>
                                <button
                                  onClick={() => copyToClipboard(image.image_url)}
                                  className="flex-shrink-0 text-accent-400 hover:text-accent-300 text-sm font-medium whitespace-nowrap ml-2"
                                >
                                  {t('images.copy')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-yellow-500/12 border border-yellow-500/20 rounded-md p-3">
                              <p className="text-sm text-yellow-400">
                                {t('images.imageUrlUnavailable')}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Repository URL */}
                        {image.repository.url && (
                          <div className="mt-2">
                            <p className="text-sm text-text-tertiary">
                              Repository: <a
                                href={sanitizeRepositoryUrl(image.repository.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent-400 hover:text-accent-300"
                              >
                                {sanitizeRepositoryUrl(image.repository.url)}
                              </a>
                            </p>
                          </div>
                        )}

                        {/* Build Info */}
                        <div className="mt-3 flex flex-wrap gap-4 text-sm text-text-tertiary">
                          {image.build_info.duration_seconds && (
                            <span>{t('images.duration', { duration: formatDuration(image.build_info.duration_seconds) })}</span>
                          )}
                          {image.build_info.completed_at && (
                            <span>{t('images.completed', { date: formatDate(image.build_info.completed_at) })}</span>
                          )}
                        </div>

                        {/* Error message */}
                        {image.error_message && (
                          <div className="mt-3 p-3 bg-red-500/12 border border-red-500/20 rounded-md">
                            <p className="text-sm text-red-400">
                              <strong>{t('images.errorLabel')}</strong> {image.error_message}
                            </p>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Pagination */}
                {imagesData.pagination.total > limit && (
                  <div className="bg-surface-2 px-4 py-3 flex items-center justify-between border-t border-border-1 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                        disabled={currentPage === 0}
                        className="relative inline-flex items-center px-4 py-2 border border-border-1 text-sm font-medium rounded-md text-text-secondary bg-surface-3 hover:bg-surface-3 disabled:opacity-50"
                      >
                        {t('images.previous')}
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={!imagesData.pagination.has_more}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-border-1 text-sm font-medium rounded-md text-text-secondary bg-surface-3 hover:bg-surface-3 disabled:opacity-50"
                      >
                        {t('images.next')}
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-text-secondary">
                          {t('images.showing')}{' '}
                          <span className="font-medium">{currentPage * limit + 1}</span> {t('images.to')}{' '}
                          <span className="font-medium">
                            {Math.min((currentPage + 1) * limit, imagesData.pagination.total)}
                          </span>{' '}
                          {t('images.of')} <span className="font-medium">{imagesData.pagination.total}</span> {t('images.results')}
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-card -space-x-px">
                          <button
                            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                            disabled={currentPage === 0}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border-1 bg-surface-3 text-sm font-medium text-text-tertiary hover:bg-surface-3 disabled:opacity-50"
                          >
                            {t('images.previous')}
                          </button>
                          <button
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={!imagesData.pagination.has_more}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border-1 bg-surface-3 text-sm font-medium text-text-tertiary hover:bg-surface-3 disabled:opacity-50"
                          >
                            {t('images.next')}
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
    </>
  );
};

export default BuiltImagesPage;